import React, { useEffect, useState, useCallback } from 'react'

export interface TourStep {
  selector: string  // data-tour attribute value (e.g. "carrier-field")
  title: string
  body: string
  placement?: 'top' | 'bottom' | 'left' | 'right'
}

interface Props {
  steps: TourStep[]
  open: boolean
  onClose: () => void
  onComplete?: () => void
}

const PADDING = 8

const GuidedTour: React.FC<Props> = ({ steps, open, onClose, onComplete }) => {
  const [step, setStep] = useState(0)
  const [rect, setRect] = useState<DOMRect | null>(null)

  const measure = useCallback(() => {
    if (!open || step >= steps.length) return
    const sel = steps[step].selector
    const el = document.querySelector(`[data-tour="${sel}"]`) as HTMLElement | null
    if (!el) {
      // Element not in DOM yet — retry after a short delay (up to 3s)
      setRect(null)
      const retryTimeout = setTimeout(() => {
        const retryEl = document.querySelector(`[data-tour="${sel}"]`) as HTMLElement | null
        if (retryEl) {
          retryEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
          setTimeout(() => setRect(retryEl.getBoundingClientRect()), 300)
        }
      }, 500)
      return () => clearTimeout(retryTimeout)
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setTimeout(() => setRect(el.getBoundingClientRect()), 300)
  }, [open, step, steps])

  useEffect(() => { measure() }, [measure])
  useEffect(() => {
    if (!open) return
    const onResize = () => measure()
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onResize, true)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onResize, true)
    }
  }, [open, measure])

  if (!open) return null
  const current = steps[step]
  if (!current) return null

  const finish = () => {
    onComplete?.()
    onClose()
  }

  // Tooltip placement
  let tooltipStyle: React.CSSProperties = {}
  if (rect) {
    const placement = current.placement || 'bottom'
    const tooltipW = 360
    const tooltipH = 200
    const vw = window.innerWidth
    const vh = window.innerHeight
    let top = 0, left = 0
    if (placement === 'bottom') {
      top = rect.bottom + PADDING + 12
      left = Math.min(Math.max(rect.left, 16), vw - tooltipW - 16)
    } else if (placement === 'top') {
      top = rect.top - tooltipH - 12
      left = Math.min(Math.max(rect.left, 16), vw - tooltipW - 16)
    } else if (placement === 'right') {
      top = Math.min(Math.max(rect.top, 16), vh - tooltipH - 16)
      left = rect.right + PADDING + 12
    } else {
      top = Math.min(Math.max(rect.top, 16), vh - tooltipH - 16)
      left = rect.left - tooltipW - 12
    }
    // Fallback if off-screen
    if (top + tooltipH > vh - 16) top = Math.max(16, vh - tooltipH - 16)
    if (left < 16) left = 16
    tooltipStyle = { top, left, width: tooltipW }
  }

  return (
    <div className="fixed inset-0 z-[70] pointer-events-none">
      {/* Spotlight overlay using SVG mask */}
      {rect && (
        <svg className="absolute inset-0 w-full h-full pointer-events-auto" style={{ width: '100vw', height: '100vh' }}>
          <defs>
            <mask id="tour-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={rect.left - PADDING}
                y={rect.top - PADDING}
                width={rect.width + PADDING * 2}
                height={rect.height + PADDING * 2}
                rx="12"
                fill="black"
              />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(15, 23, 42, 0.65)" mask="url(#tour-mask)" />
          {/* Highlight ring around target */}
          <rect
            x={rect.left - PADDING}
            y={rect.top - PADDING}
            width={rect.width + PADDING * 2}
            height={rect.height + PADDING * 2}
            rx="12"
            fill="none"
            stroke="#14b8a6"
            strokeWidth="3"
            className="animate-pulse"
          />
        </svg>
      )}

      {/* Fallback dim layer if element not found */}
      {!rect && <div className="absolute inset-0 bg-slate-900/65 pointer-events-auto" />}

      {/* Tooltip card */}
      <div
        className="absolute bg-white rounded-2xl shadow-2xl border border-slate-200 pointer-events-auto overflow-hidden"
        style={rect ? tooltipStyle : { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 380 }}
      >
        <div className="bg-gradient-to-r from-teal-500 to-emerald-500 px-5 py-3 text-white">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-wider opacity-90">
              Step {step + 1} of {steps.length}
            </div>
            <button onClick={finish} className="text-white/80 hover:text-white text-xs font-semibold">Skip</button>
          </div>
        </div>
        <div className="px-5 py-4">
          <h3 className="font-bold text-slate-900 text-base mb-1.5">{current.title}</h3>
          <p className="text-sm text-slate-600 leading-relaxed">{current.body}</p>
          <div className="flex items-center justify-between mt-5">
            <div className="flex gap-1.5">
              {steps.map((_, i) => (
                <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === step ? 'bg-teal-500 w-4' : 'bg-slate-200'}`} />
              ))}
            </div>
            <div className="flex gap-2">
              {step > 0 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="px-3 py-1.5 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Back
                </button>
              )}
              <button
                onClick={() => (step < steps.length - 1 ? setStep(step + 1) : finish())}
                className="px-4 py-1.5 text-xs font-bold text-white bg-teal-500 hover:bg-teal-600 rounded-lg transition-colors shadow-sm shadow-teal-500/30"
              >
                {step < steps.length - 1 ? 'Next →' : 'Finish'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GuidedTour

export const FIRST_LMN_TOUR: TourStep[] = [
  {
    selector: 'payer-section',
    title: '👋 Welcome — let\'s draft your first LMN',
    body: 'I\'ll walk you through generating your first letter in under 2 minutes. Start here in Section 1: tell us which insurance carrier denied the medication.',
    placement: 'right',
  },
  {
    selector: 'carrier-field',
    title: 'Pick the insurance carrier',
    body: 'Select the payer that issued the denial. We have 62 payers wired with their exact appeal addresses, fax lines, and policy criteria. Choosing the right one unlocks payer-specific language in your letter.',
    placement: 'right',
  },
  {
    selector: 'icd10-field',
    title: 'Add the ICD-10 diagnosis',
    body: 'Type the diagnosis name or code — autocomplete handles the rest. The first code is treated as primary. This drives our citation engine to attach the right clinical evidence.',
    placement: 'right',
  },
  {
    selector: 'drug-field',
    title: 'Enter the denied medication',
    body: 'Start typing the drug name. You\'ll see matches from our 558-drug catalog, including biosimilars and brand/generic equivalents. Picking from the dropdown unlocks pre-loaded clinical justification.',
    placement: 'right',
  },
  {
    selector: 'denial-field',
    title: 'Choose the denial reason',
    body: 'Pick the reason the payer cited (non-formulary, step therapy, not medically necessary, etc.). This is the single biggest predictor of your appeal\'s success — it tells our model exactly which counter-argument to lead with.',
    placement: 'right',
  },
  {
    selector: 'generate-button',
    title: '🚀 Hit Generate',
    body: 'Click here and a fully-cited LMN appears in ~5 seconds. Then add patient name and DOB after downloading — never on this platform. Ready?',
    placement: 'top',
  },
]
